import { useContext, useEffect, useState } from "react";

import { useCachedAdminApi } from "../../../../../common/api/admin-api/useCachedAdminApi";
import { useDeliveryApi } from "../../../../../common/api/delivery-api/useDeliveryApi";
import { useMasterLookupsApi } from "../../../../../common/api/master-api/lookups/useMasterLookupsApi";
import { formOptionsLoader, useAreOptionsLoading } from "../../../../../common/components/form/FormOptionsLoader";
import { AspectRatio } from "../../../../../common/data/lookup-data-providers/AspectRatiosDataProvider";
import { AspectType } from "../../../../../common/data/lookup-data-providers/AspectTypesDataProvider";
import { LanguagesDataContext } from "../../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { Originator } from "../../../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { Airline } from "../../../../../common/types/Airline";
import { Standard } from "../../../../../common/types/Standard";
import { useCreateFileRecordState } from "./state/useCreateFileRecordState";

export const useCreateFileRecordFormOptions = () => {
    const {
        dubbingLanguages: dubbings,
        embeddedSubtitleLanguages: embeddedSubtitles,
        isLoading: areLanguagesLoading,
        subtitleLanguages: dynamicSubtitles
    } = useContext(LanguagesDataContext);

    const { isLoading: isFormLoading } = useCreateFileRecordState();

    const [standards, setStandards] = useState<Standard[]>([]);
    const [areStandardsLoading, setAreStandardsLoading] = useState(false);
    const [aspectRatios, setAspectRatios] = useState<AspectRatio[]>([]);
    const [areAspectRatiosLoading, setAreAspectRatiosLoading] = useState(false);
    const [aspectTypes, setAspectTypes] = useState<AspectType[]>([]);
    const [areAspectTypesLoading, setAreAspectTypesLoading] = useState(false);
    const [originators, setOriginators] = useState<Originator[]>([]);
    const [areOriginatorsLoading, setAreOriginatorsLoading] = useState(false);

    const [airlines, setAirlines] = useState<Airline[]>([]);
    const [areAirlinesLoading, setAreAirlinesLoading] = useState(false);

    const { getStandards, getAspectRatios, getAspectTypes } = useMasterLookupsApi();

    const { getOriginatorsLookup } = useDeliveryApi();
    const { getAirlinesLookup } = useCachedAdminApi();

    const loadStandardsOptions = (): void => formOptionsLoader(getStandards, setAreStandardsLoading, setStandards);

    const loadAspectRatiosOptions = (): void =>
        formOptionsLoader(getAspectRatios, setAreAspectRatiosLoading, setAspectRatios);

    const loadAspectTypesOptions = (): void =>
        formOptionsLoader(getAspectTypes, setAreAspectTypesLoading, setAspectTypes);

    const loadOriginatorsOptions = (): void =>
        formOptionsLoader(getOriginatorsLookup, setAreOriginatorsLoading, setOriginators);

    const loadAirlinesOptions = (): void => formOptionsLoader(getAirlinesLookup, setAreAirlinesLoading, setAirlines);

    useEffect(() => {
        loadStandardsOptions();
        loadAspectRatiosOptions();
        loadAspectTypesOptions();
        loadOriginatorsOptions();
        loadAirlinesOptions();
    }, []);

    const { areOptionsLoading } = useAreOptionsLoading([
        areStandardsLoading,
        areAspectRatiosLoading,
        areAspectTypesLoading,
        areOriginatorsLoading,
        areAirlinesLoading,
        areLanguagesLoading,
        isFormLoading
    ]);

    return {
        standards,
        embeddedSubtitles,
        dubbings,
        dynamicSubtitles,
        areStandardsLoading,
        aspectRatios,
        areAspectRatiosLoading,
        aspectTypes,
        areAspectTypesLoading,
        originators,
        areOriginatorsLoading,
        airlines,
        areAirlinesLoading,
        areOptionsLoading
    };
};
